import type { KnowledgeBase } from "@/mocks/knowledge";
import { knowledgeBases as seededBases } from "@/mocks/knowledge";

const LS_KEY = "scienceone.knowledgeBases.v1";

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function loadKnowledgeBases(): KnowledgeBase[] {
  const stored = safeParse<KnowledgeBase[]>(localStorage.getItem(LS_KEY));
  if (!stored || !Array.isArray(stored)) return seededBases;

  // Merge: stored overrides seeded by id, keep seeded order then append new.
  const byId = new Map<string, KnowledgeBase>();
  for (const b of seededBases) byId.set(b.id, b);
  for (const b of stored) byId.set(b.id, b);

  const seededIds = new Set(seededBases.map((b) => b.id));
  const merged: KnowledgeBase[] = [];
  for (const b of seededBases) merged.push(byId.get(b.id)!);
  for (const b of stored) {
    if (!seededIds.has(b.id)) merged.unshift(b);
  }
  return merged;
}

export function saveKnowledgeBases(list: KnowledgeBase[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

export function upsertKnowledgeBase(base: KnowledgeBase) {
  const list = loadKnowledgeBases();
  const next = [base, ...list.filter((b) => b.id !== base.id)];
  saveKnowledgeBases(next);
  return next;
}

export function deleteKnowledgeBase(id: string) {
  const list = loadKnowledgeBases();
  const next = list.filter((b) => b.id !== id);
  saveKnowledgeBases(next);
  return next;
}

export function renameKnowledgeBase(id: string, name: string) {
  const list = loadKnowledgeBases();
  const next = list.map((b) => (b.id === id ? { ...b, name } : b));
  saveKnowledgeBases(next);
  return next;
}

export function getKnowledgeBaseById(id: string | undefined) {
  if (!id) return null;
  return loadKnowledgeBases().find((b) => b.id === id) ?? null;
}

